import { motion } from "framer-motion";
import { 
  Landmark, 
  Building2, 
  Home, 
  Users2, 
  CircleDollarSign, 
  TrendingUp, 
  FileText, 
  Shield,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  BadgeCheck,
  FileCheck
} from "lucide-react";

export default function RegulatoryAdvisory() {
  const services = [
    {
      id: 1,
      title: "Banking & Cooperative Banking Advisory",
      icon: Landmark,
      image: "https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=800&q=80",
      subsections: [
        {
          subtitle: "Banking License & Regulatory Advisory",
          items: [
            "Advisory on formation and licensing of banking institutions",
            "Support for regulatory approvals with RBI and other authorities",
            "Preparation and review of licensing applications and documentation",
            "Advisory on promoter eligibility, capital, and governance norms"
          ]
        },
        {
          subtitle: "Cooperative Bank Registration & Advisory",
          items: [
            "Advisory on registration of cooperative banks under State / Multi-State Cooperative Acts",
            "Assistance with conversion of cooperative societies into cooperative banks",
            "Advisory on RBI, NABARD, and RCS approvals",
            "Capital structuring, governance, and compliance advisory",
            "Ongoing regulatory reporting and compliance support"
          ]
        }
      ]
    },
    {
      id: 2,
      title: "Non-Banking Financial Company (NBFC) Advisory",
      icon: Building2,
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80",
      subsections: [
        {
          subtitle: "NBFC Registration & Licensing",
          items: [
            "Advisory on NBFC registration with RBI",
            "Assistance with NBFC classification (NBFC-ICC, NBFC-MFI, NBFC-Factor, NBFC-IDF, etc.)",
            "Preparation of application, business plans, and financial projections",
            "Net Owned Funds (NOF) and capital adequacy advisory"
          ]
        },
        {
          subtitle: "NBFC Compliance & Strategic Advisory",
          items: [
            "RBI compliance and periodic reporting advisory",
            "Governance, risk management, and internal control frameworks",
            "Advisory on change in management, shareholding, or business activities",
            "Mergers, acquisitions, and restructuring advisory for NBFCs"
          ]
        }
      ]
    },
    {
      id: 3,
      title: "Housing Finance Company (HFC) Advisory",
      icon: Home,
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80",
      subsections: [
        {
          subtitle: "HFC Registration & Licensing (RBI)",
          items: [
            "Advisory on registration of Housing Finance Companies under RBI regulations",
            "Assistance with licensing documentation and regulatory submissions",
            "Capital adequacy, NOF, and prudential norms advisory",
            "Business model and product structuring advisory"
          ]
        },
        {
          subtitle: "HFC Compliance & Operational Advisory",
          items: [
            "Ongoing RBI compliance and regulatory reporting support",
            "Credit policy, risk management, and asset liability management (ALM) advisory",
            "Governance, internal audit, and compliance framework support",
            "Advisory on expansion, restructuring, or conversion"
          ]
        }
      ]
    },
    {
      id: 4,
      title: "Microfinance & NBFC-MFI Advisory",
      icon: Users2,
      image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&q=80",
      subsections: [
        {
          subtitle: "Microfinance Institution Registration",
          items: [
            "Advisory on registration of Microfinance Institutions as NBFC-MFIs",
            "Assistance with RBI licensing and classification norms",
            "Business model structuring in line with microfinance regulations",
            "Capital and promoter eligibility advisory"
          ]
        },
        {
          subtitle: "Regulatory Compliance & Growth Advisory",
          items: [
            "Advisory on microfinance regulatory compliance and reporting",
            "Fair Practices Code and customer protection advisory",
            "Portfolio quality, risk management, and recovery strategy advisory",
            "Advisory on scaling operations and geographic expansion"
          ]
        }
      ]
    },
    {
      id: 5,
      title: "Nidhi Company Registration & Advisory",
      icon: CircleDollarSign,
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      items: [
        "Advisory on formation and registration of Nidhi companies",
        "Assistance with incorporation, MCA filings, and approvals",
        "Compliance with Nidhi Rules, deposit norms, and member requirements",
        "Ongoing statutory and regulatory compliance support"
      ]
    },
    {
      id: 6,
      title: "Alternative Investment Fund (AIF) Advisory (SEBI)",
      icon: TrendingUp,
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80",
      subsections: [
        {
          subtitle: "AIF Registration Advisory",
          items: [
            "Advisory on SEBI registration for AIFs (Category I, II, III)",
            "Structuring of trust, LLP, or company structures",
            "Preparation and review of Private Placement Memorandum (PPM)",
            "Sponsor, manager, and trustee structuring advisory"
          ]
        },
        {
          subtitle: "AIF Compliance & Operations",
          items: [
            "SEBI compliance and periodic reporting advisory",
            "Fund governance and risk management advisory",
            "Investor onboarding and disclosure compliance",
            "Regulatory amendment and approval support"
          ]
        }
      ]
    },
    {
      id: 7,
      title: "Non-Convertible Debentures (NCDs) & Debt Advisory",
      icon: FileText,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
      subsections: [
        {
          subtitle: "NCD Structuring & Issuance Advisory",
          items: [
            "Advisory on issuance of Non-Convertible Debentures",
            "Private placement and public issue advisory",
            "Documentation support (Offer Documents, IM, Debenture Trust Deed)",
            "Credit rating, trustee, and listing coordination"
          ]
        },
        {
          subtitle: "Post-Issuance Compliance",
          items: [
            "SEBI and stock exchange compliance advisory",
            "Ongoing disclosure and covenant compliance",
            "Redemption, rollover, and restructuring advisory"
          ]
        }
      ]
    },
    {
      id: 8,
      title: "End-to-End Regulatory & Compliance Support",
      icon: Shield,
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80",
      items: [
        "Liaison with RBI, SEBI, MCA, NABARD, NHB (where applicable), and Stock Exchanges",
        "Preparation of regulatory policies, manuals, and filings",
        "Advisory on inspections, audits, and supervisory observations",
        "Representation support for regulatory queries and notices"
      ]
    }
  ];

  const regulators = [
    { name: "RBI", fullName: "Reserve Bank of India" },
    { name: "SEBI", fullName: "Securities and Exchange Board of India" },
    { name: "NABARD", fullName: "National Bank for Agriculture and Rural Development" },
    { name: "NHB", fullName: "National Housing Bank" },
    { name: "MCA", fullName: "Ministry of Corporate Affairs" },
    { name: "RCS", fullName: "Registrar of Cooperative Societies" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-50 via-white to-yellow-50 border-b-2 border-yellow-200 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-96 h-96 bg-yellow-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-8">
              <div className="p-5 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full shadow-2xl">
                <BadgeCheck className="w-20 h-20 text-white" />
              </div>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6 leading-tight">
              Registration, Licensing & <br />
              <span className="text-yellow-600">Regulatory Advisory Services</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              Banks, Financial Institutions & Capital Market Entities
            </p>
            
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <div className="flex items-center space-x-2 bg-white px-5 py-3 rounded-full shadow-lg border-2 border-yellow-200">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                <span className="text-gray-700 font-semibold">RBI Approved</span>
              </div>
              <div className="flex items-center space-x-2 bg-white px-5 py-3 rounded-full shadow-lg border-2 border-yellow-200">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                <span className="text-gray-700 font-semibold">SEBI Compliant</span>
              </div>
              <div className="flex items-center space-x-2 bg-white px-5 py-3 rounded-full shadow-lg border-2 border-yellow-200">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                <span className="text-gray-700 font-semibold">End-to-End Support</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Regulators Section */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 py-16"
      >
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Regulatory Authorities We Work With
          </h2>
          <p className="text-yellow-100 text-center text-lg mb-10 max-w-3xl mx-auto">
            Expert liaison and compliance support across all major financial regulators
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {regulators.map((regulator, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-5 text-center shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex justify-center mb-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FileCheck className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-black mb-1">
                  {regulator.name}
                </h3>
                <p className="text-gray-600 text-xs leading-tight">
                  {regulator.fullName}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Services Grid */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
        variants={containerVariants}
        className="max-w-7xl mx-auto px-6 py-20"
      >
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-yellow-100 px-4 py-2 rounded-full mb-4"
          >
            <Shield className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-700 font-semibold">8 Specialized Service Areas</span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            Comprehensive Regulatory Advisory
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From registration to ongoing compliance - complete support for financial institutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border-2 border-gray-100 hover:border-yellow-400"
              >
                {/* Image Section */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 right-4">
                    <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl shadow-lg">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white leading-tight">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6">
                  {service.subsections ? (
                    // Services with subsections
                    <div className="space-y-5">
                      {service.subsections.map((subsection, subIdx) => (
                        <div key={subIdx}>
                          <h4 className="text-sm font-bold text-yellow-600 mb-2.5 flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                            <span>{subsection.subtitle}</span>
                          </h4>
                          <ul className="space-y-2">
                            {subsection.items.map((item, idx) => (
                              <li key={idx} className="flex items-start space-x-2.5 group/item">
                                <ChevronRight className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0 group-hover/item:translate-x-1 transition-transform" />
                                <span className="text-gray-700 text-sm leading-relaxed">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Regular services
                    <ul className="space-y-3">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3 group/item">
                          <ChevronRight className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0 group-hover/item:translate-x-1 transition-transform" />
                          <span className="text-gray-700 text-base leading-relaxed">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Bottom Accent */}
                <div className="px-6 pb-6">
                  <div className="h-1.5 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.section>

      {/* Disclaimer Section */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="max-w-7xl mx-auto px-6 pb-20"
      >
        <div className="bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-200 rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="p-3 bg-yellow-100 rounded-xl">
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-black mb-4">Important Disclaimer</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                All regulatory, registration, and compliance advisory services are provided in accordance with applicable laws and regulations. <strong className="text-black">Final approvals remain subject to the discretion of the respective regulatory authorities.</strong>
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 text-white py-24 overflow-hidden"
      >
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Navigate Regulatory Compliance with Confidence
          </h2>
          <p className="text-xl md:text-2xl mb-10 text-yellow-50 max-w-3xl mx-auto">
            Expert guidance for registration, licensing, and ongoing regulatory compliance across all financial sectors
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-10 py-4 bg-white text-yellow-600 font-bold text-lg rounded-xl hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl">
              Schedule Consultation
            </button>
            <button className="px-10 py-4 bg-transparent border-2 border-white text-white font-bold text-lg rounded-xl hover:bg-white hover:text-yellow-600 transform hover:scale-105 transition-all duration-300">
              Download Advisory Guide
            </button>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
